import Icon from "components/icon";
import { getRefId, type INotification, type ISavedDocument } from "findef";
import { NotificationItem } from "./components/notification-item";
import { useNotifications, type UseNotifications } from "~/composables/useNotifications";

const TopBar = defineComponent(() => {
  return () => {
    return (
      <div class="flex-none px-5 pt-4 pb-3 border-b border-slate-900/[0.06] cursor-default">
        <h2 class="text-[15px] font-semibold tracking-tight text-slate-900 dark:text-white">
          Notifications
        </h2>
      </div>
    );
  };
});

const EmptyState = defineComponent(() => {
  return () => {
    return (
      <div class="w-full h-full grid items-center justify-center py-12">
        <span class="notif-empty-fade">
          <div class="grid items-center justify-center">
            <div
              class="rounded-full bg-gray-100 text-gray-400 mb-3 w-16 h-16 text-center flex justify-center items-center notif-bell-breath"
            >
              <Icon
                src={{
                  src: async () => import("assets/img/svg/icons/bell.svg?url"),
                  stroke: "none",
                  size: "2rem",
                  fill: "currentColor",
                }}
              />
            </div>
          </div>
          <div class="text-center">
            <h2 class="text-lg font-semibold">
              <span class="text-sm text-gray-900 font-semibold">
                All clear
              </span>
            </h2>
            <p class="text-gray-500 text-base">
              <span class="text-sm text-gray-500">
                You're up to date on notifications
              </span>
            </p>
          </div>
        </span>
      </div>
    )
  };
});


const ListBody = defineComponent<{
  notifications: MaybeRef<Array<ISavedDocument<INotification, string>>>;
  leaving?: MaybeRef<boolean>;
}>((props) => {
  const notifications = computed((): Array<ISavedDocument<INotification, string>> => unref(props.notifications) || []);
  const leaving = computed(() => !!unref(props.leaving));

  return () => {
    return <div class="flex-[2rem] flex flex-col">
      {
        notifications.value.length <= 0 ? (<EmptyState/>) : (
          <div class="w-full flex-[2rem] overflow-y-auto pretty-scrollbar">
            {
              notifications.value.map((item, index) => {
                const id = getRefId(item);
                const total = notifications.value.length;
                const enterDelay = Math.min(index, 7) * 28;
                const leaveDelay = Math.min(total - 1 - index, 5) * 18;
                return (
                  <div
                    class={leaving.value ? "notif-item-leave" : "notif-item-enter"}
                    style={{ animationDelay: `${leaving.value ? leaveDelay : enterDelay}ms` }}
                    key={`notification-${id}`}
                  >
                    <NotificationItem notification={item}/>
                  </div>
                );
              })
            }
          </div>
        )
      }
    </div>;
  }
}, { props: ['notifications', 'leaving'] })

export const NotificationsList = defineComponent<{
  hook?: UseNotifications,
  withHeadline?: boolean,
  isActive?: MaybeRef<boolean>,
}>((props) => {
  const hook = props.hook || useNotifications();
  const notifications = computed((): Array<ISavedDocument<INotification, string>> => hook.notifications.value || []);
  const isActive = computed(() => unref(props.isActive));
  // `leaving` is true only after the panel has been opened at least once and is now closing.
  // Avoids playing the leave animation on first mount when isActive is initially false/undefined.
  const wasActive = ref<boolean>(false);
  const leaving = ref<boolean>(false);
  watch(isActive, (next) => {
    if (next) {
      wasActive.value = true;
      leaving.value = false;
    } else if (wasActive.value) {
      leaving.value = true;
    }
  }, { immediate: true });

  return () => {
    return (
      <div
        class="w-full h-full bg-white flex flex-col"
      >
        { props.withHeadline === false ? null : <TopBar /> }
        <ListBody notifications={notifications} leaving={leaving.value}/>
      </div>
    );
  };
}, { props: ['hook', 'withHeadline', 'isActive'] });
